import { EventSourcePolyfill } from "event-source-polyfill";
import { useEffect, useRef, useState } from "react";

import InfoIcon from "@mui/icons-material/Info";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";

import { MarketingSummaryAnalyticsService } from "@analytics/index";

import { openaiChat } from "@api/chat.api";
import { getOrganization } from "@api/organization.api";

import AIChip from "@components/AIChip/AIChip";
import Overlay from "@components/Overlay/Overlay";
import {
  TableToolbar,
  TableToolbarLeft,
  TableToolbarRight,
  TableToolbarTitle,
} from "@components/TableToolbar/TableToolbar";

import C from "@constants/constants";

import { AvailableChatModelsType } from "@interfaces/chat.type";

import ChatInput from "@pages/Technology/components/ChatInput";
import ChatUsageWarning from "@pages/Technology/components/ChatUsageWarning";
import TechnologyMarketingSummaryMessages from "@pages/Technology/components/TechnologyMarketingSummaryMessages";
import { handleEventListener } from "@pages/Technology/components/utils";

import { useLoadMessages, usePersistMessages } from "@queries/chat.query";

import { useOrganizationStore } from "@store/organizationStore";
import { PopupActions } from "@store/popupStore";

import theme from "@theme/index";

export interface IMessage {
  content: string;
  fileName?: string;
  id: string;
  isFile?: boolean;
  isRated?: boolean;
  role: "assistant" | "instruction" | "system" | "user";
}

interface ITechnologyMarketingSummaryProperties {
  technologyId: string;
}

const outerWrapperStyles = {
  backgroundColor: theme.palette.common.white,
  display: "flex",
  flexDirection: "column",
  height: "calc(100vh - 292px)",
  justifyContent: "space-evenly",
};

const innerWrapperStyles = {
  display: "flex",
  flexDirection: "column",
  height: "100%",
  mx: "auto",
  overflow: "hidden",
  width: "100%",
};

const TechnologyMarketingSummary = ({ technologyId }: ITechnologyMarketingSummaryProperties) => {
  const [isStreaming, setIsStreaming] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeletingChatHistory, setIsDeletingChatHistory] = useState(false);
  const { organization } = useOrganizationStore();

  const setOrganization = useOrganizationStore((state) => state.actions.setOrganization);
  const { mutateAsync: persistMessages } = usePersistMessages();

  const spentTokens = organization?.chatUsageSpentTokens;
  const totalTokens = organization?.chatUsageLimitTokens;

  const isChatUsageLimitReached = spentTokens && totalTokens && spentTokens >= totalTokens;

  const handleIsDeletingChatHistory = (value: boolean) => {
    setIsDeletingChatHistory(value);
  };

  const isInitialLoad = useRef(true);

  const { isLoadingMessages, messages, setMessages, summaryId } = useLoadMessages(technologyId);

  const handleSendMessages = async (
    value: string,
    file: { fileContent: string; fileName: string } | null,
    handleRemoveFile: () => void,
  ) => {
    if (value.trim() === "") return;

    try {
      setIsLoading(true);
      handleRemoveFile();

      const messageId = Date.now().toString() + Math.random().toString();

      const fileContentMessage = file
        ? [
            {
              content: file.fileContent,
              fileName: file.fileName,
              id: Date.now().toString() + Math.random().toString(),
              isFile: true,
              role: "system",
            } as const,
          ]
        : [];

      setMessages((previousMessages) => [
        ...previousMessages,
        { content: value, id: messageId, role: "user" },
        ...fileContentMessage,
      ]);

      eventSourceHandler();

      await openaiChat({
        messages: [...fileContentMessage, ...(messages ?? []), { content: value, id: messageId, role: "user" }],
        model: (organization?.chatModel ?? "openai") as AvailableChatModelsType,
        summaryId,
        technologyId,
      });
      MarketingSummaryAnalyticsService.trackSummaryMessageSent();
    } catch (error) {
      if (error instanceof Error && error.message === "Chat usage limit exceeded") {
        PopupActions.openConfirmation({
          content: "Chat usage limit exceeded",
          title: "Chat Usage Limit Exceeded",
        });
        setIsStreaming(false);
        setIsLoading(false);
      }
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };

  const eventSourceReference = useRef<EventSourcePolyfill | null>(null);

  const stopGenerating = () => {
    if (eventSourceReference.current) {
      eventSourceReference.current.close();
      setIsStreaming(false);
    }
  };

  const eventSourceHandler = () => {
    setIsStreaming(true);

    const eventSource = new EventSourcePolyfill(`${C.VITE_APP_NEST_SERVER}/v2/openai/chat-stream`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem(C.AUTH_TOKEN_KEY)}`,
      },
    });
    eventSourceReference.current = eventSource;

    const listener = handleEventListener({
      setIsLoading,
      setMessages,
    });

    eventSource.addEventListener("ai" as keyof EventSourceEventMap, listener);

    eventSource.addEventListener("error", () => {
      eventSource.removeEventListener("ai" as keyof EventSourceEventMap, listener);
      eventSource.close();
      setIsStreaming(false);
      eventSourceReference.current = null;
    });
  };

  useEffect(() => {
    if (isStreaming || isInitialLoad.current) {
      isInitialLoad.current = false;
      return;
    }
    try {
      if (!technologyId || !messages) return;
      void persistMessages({
        messages,
        technologyId,
      });
      void getOrganization(organization?.id.toString() ?? "").then((organization) => {
        setOrganization(organization);
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to persist chat messages:", error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStreaming, technologyId]);

  return (
    <>
      <Overlay isVisible={isDeletingChatHistory} title={"Deleting Chat History"} />
      <TableToolbar>
        <TableToolbarLeft>
          <TableToolbarTitle resourceLink={"Link"} tableTitle="Marketing Summary" workflow="marketing-summary" />
        </TableToolbarLeft>
        <TableToolbarRight>
          <Box
            sx={{
              alignItems: "flex-end",
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
            }}
          >
            <AIChip />
            <Typography sx={{ alignItems: "center", display: "flex" }} variant="body2">
              <InfoIcon sx={{ color: theme.palette.info.main, fontSize: "18px", marginRight: "5px" }} />
              <span>
                All information is private, secure, and{" "}
                <b>
                  <u>never</u>
                </b>{" "}
                used for model training.
              </span>
            </Typography>
          </Box>
        </TableToolbarRight>
      </TableToolbar>
      <ChatUsageWarning />
      {isLoadingMessages ? (
        <Overlay isVisible={isLoadingMessages} title={"Loading Chat Messages"} />
      ) : (
        <Box sx={outerWrapperStyles}>
          <Box sx={innerWrapperStyles}>
            <Box sx={{ flex: 1, overflowY: "auto" }}>
              <TechnologyMarketingSummaryMessages
                isStreaming={isStreaming}
                messages={messages ?? []}
                summaryId={summaryId}
                technologyId={technologyId}
              />
            </Box>
            {isLoading && (
              <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}
            {!isChatUsageLimitReached && (
              <ChatInput
                canDelete={messages?.length > 1}
                handleIsDeletingChatHistory={handleIsDeletingChatHistory}
                handleSendMessages={handleSendMessages}
                isStreaming={isStreaming}
                stopGenerating={stopGenerating}
                technologyId={technologyId}
              />
            )}
          </Box>
        </Box>
      )}
    </>
  );
};

export default TechnologyMarketingSummary;
